import {
  useGetProjectTeamQuery,
  useGetProjectInvitationsQuery,
  useSendInvitationsMutation,
  useUpdateTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useResendInvitationMutation,
  useCancelInvitationMutation,
} from '@/services/teamApi';
import {
  AddTeamMemberData,
  MemberStatus,
  ProjectMember,
  TeamMemberInvitation,
  TeamRole,
  UpdateTeamMemberData,
} from '@/types';
import { Check, Clock, Mail, Plus, RefreshCw, Search, Users, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import LoadingSpinner from '../LoadingSpinner';
import Pagination from '../Pagination';
import { Input } from '../forms';
import AddTeamMemberDrawer from './AddTeamMemberDrawer';
import TeamMemberCard from './TeamMemberCard';

interface TeamManagementProps {
  projectId: string;
  currentUserCanManage: boolean;
}

const TeamManagement: React.FC<TeamManagementProps> = ({ projectId, currentUserCanManage }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<MemberStatus | 'all'>('all');
  const [roleFilter, setRoleFilter] = useState<TeamRole | 'all'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState<ProjectMember | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // RTK Query hooks
  const {
    data: teamData,
    isLoading: isLoadingTeam,
    refetch: refetchTeam,
  } = useGetProjectTeamQuery({
    projectId,
    page: currentPage,
    limit: itemsPerPage,
    search: searchQuery || undefined,
    role: roleFilter !== 'all' ? roleFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    includeInactive: true,
  });

  const {
    data: invitationsData,
    isLoading: isLoadingInvitations,
    refetch: refetchInvitations,
  } = useGetProjectInvitationsQuery({ projectId });

  // Mutations
  const [sendInvitations] = useSendInvitationsMutation();
  const [updateTeamMember] = useUpdateTeamMemberMutation();
  const [removeTeamMember] = useRemoveTeamMemberMutation();
  const [resendInvitation] = useResendInvitationMutation();
  const [cancelInvitation] = useCancelInvitationMutation();

  // Process members data
  const members = useMemo((): ProjectMember[] => {
    if (!teamData?.success) return [];
    const rawData = teamData.data?.data || [];
    return rawData.map((member: any) => ({
      ...member,
      id: member._id || member.id,
      user: {
        ...member.user,
        id: member.user?._id || member.user?.id,
        firstName: member.user?.firstName || 'Unknown',
        lastName: member.user?.lastName || 'User',
        name:
          member.user?.name ||
          `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim(),
        company: member.user?.companyName || '',
        title: member.user?.companyType || '',
        expertise: [],
        isActive: member.user?.isActive ?? true,
      },
    }));
  }, [teamData]);

  // Process invitations data
  const invitations = useMemo((): TeamMemberInvitation[] => {
    if (!invitationsData?.success || !invitationsData.data) return [];
    return invitationsData.data.map((inv: any) => ({
      id: inv._id || inv.id,
      email: inv.email,
      role: inv.role,
      permissions: inv.permissions,
      invitedBy: inv.invitedBy,
      invitedAt: new Date(inv.invitedAt || inv.createdAt),
      expiresAt: new Date(inv.expiresAt),
      status: inv.status,
      message: inv.message,
    }));
  }, [invitationsData]);

  const isLoading = isLoadingTeam || isLoadingInvitations;

  // Refresh data
  const loadTeamData = () => {
    refetchTeam();
    refetchInvitations();
  };

  const handleAddMember = async (data: AddTeamMemberData | UpdateTeamMemberData) => {
    try {
      const addData = data as AddTeamMemberData;
      const result = await sendInvitations({
        projectId,
        emails: [addData.email],
        role: addData.role,
        permissions: addData.permissions,
        message: addData.message,
      }).unwrap();

      if (result.success) {
        setSuccessMessage(result.message || 'Invitation sent successfully');
      } else {
        setErrorMessage('Failed to send invitation');
      }
    } catch (error: any) {
      console.error('Error adding team member:', error);
      setErrorMessage(error.data?.message || 'Failed to add team member');
    }
  };

  const handleEditMember = async (data: AddTeamMemberData | UpdateTeamMemberData) => {
    if (!editingMember) return;

    try {
      const updateData = data as UpdateTeamMemberData;
      const result = await updateTeamMember({
        projectId,
        memberId: editingMember.id,
        role: updateData.role,
        permissions: updateData.permissions,
        status: updateData.status,
      }).unwrap();

      if (result.success) {
        setSuccessMessage(result.message || 'Team member updated successfully');
        setEditingMember(null);
      } else {
        setErrorMessage('Failed to update team member');
      }
    } catch (error: any) {
      console.error('Error updating team member:', error);
      setErrorMessage(error.data?.message || 'Failed to update team member');
    }
  };

  const handleRemoveMember = async (member: ProjectMember) => {
    if (
      !confirm(
        `Are you sure you want to remove ${member.user?.firstName} ${member.user?.lastName} from this project?`
      )
    ) {
      return;
    }

    try {
      const result = await removeTeamMember({
        projectId,
        memberId: member.id,
      }).unwrap();

      if (result.success) {
        setSuccessMessage(result.message || 'Team member removed successfully');
      } else {
        setErrorMessage('Failed to remove team member');
      }
    } catch (error: any) {
      console.error('Error removing team member:', error);
      setErrorMessage(error.data?.message || 'Failed to remove team member');
    }
  };

  const handleStatusToggle = async (member: ProjectMember, newStatus: MemberStatus) => {
    try {
      const result = await updateTeamMember({
        projectId,
        memberId: member.id,
        status: newStatus,
      }).unwrap();

      if (result.success) {
        setSuccessMessage(
          `Member ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`
        );
      } else {
        setErrorMessage('Failed to update member status');
      }
    } catch (error: any) {
      console.error('Error updating member status:', error);
      setErrorMessage(error.data?.message || 'Failed to update member status');
    }
  };

  const handleResendInvitation = async (invitation: TeamMemberInvitation) => {
    try {
      const result = await resendInvitation({
        invitationId: invitation.id,
      }).unwrap();

      if (result.success) {
        setSuccessMessage('Invitation resent successfully');
      } else {
        setErrorMessage('Failed to resend invitation');
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      setErrorMessage(error.data?.message || 'Failed to resend invitation');
    }
  };

  const handleCancelInvitation = async (invitation: TeamMemberInvitation) => {
    try {
      const result = await cancelInvitation({
        invitationId: invitation.id,
        projectId,
      }).unwrap();

      if (result.success) {
        setSuccessMessage('Invitation cancelled successfully');
      } else {
        setErrorMessage('Failed to cancel invitation');
      }
    } catch (error: any) {
      console.error('Error cancelling invitation:', error);
      setErrorMessage(error.data?.message || 'Failed to cancel invitation');
    }
  };

  // Filter members
  const filteredMembers = members.filter(member => {
    const matchesSearch =
      !searchQuery ||
      `${member.user?.firstName} ${member.user?.lastName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      member.user?.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.user?.company?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || member.status === statusFilter;
    const matchesRole = roleFilter === 'all' || member.role === roleFilter;

    return matchesSearch && matchesStatus && matchesRole;
  });

  // Pagination calculations
  const totalItems = filteredMembers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, roleFilter]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getStatusStats = () => {
    return {
      active: members.filter(m => m.status === 'active').length,
      inactive: members.filter(m => m.status === 'inactive').length,
      pending: members.filter(m => m.status === 'pending').length,
      total: members.length,
    };
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(date));
  };

  const stats = getStatusStats();

  return (
    <div className="absolute inset-0 flex flex-col">
      {/* Success/Error Messages - Compact Toast Style */}
      {successMessage && (
        <div className="absolute top-2 right-4 z-50 bg-green-50 border border-green-200 rounded-md px-3 py-2 shadow-sm">
          <div className="flex items-center space-x-2">
            <span className="text-green-700 text-xs font-medium">{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-500 hover:text-green-700 transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="absolute top-2 right-4 z-50 bg-red-50 border border-red-200 rounded-md px-3 py-2 shadow-sm">
          <div className="flex items-center space-x-2">
            <span className="text-red-700 text-xs font-medium">{errorMessage}</span>
            <button
              onClick={() => setErrorMessage(null)}
              className="text-red-500 hover:text-red-700 transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {/* Compact Header Bar */}
      <div className="flex-none bg-white border-b border-gray-200">
        <div className="px-5 py-2.5">
          <div className="flex items-center justify-between">
            {/* Left: Title + Inline Stats */}
            <div className="flex items-center space-x-5">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center flex-none">
                  <Users className="h-4 w-4 text-brand-600" />
                </div>
                <div>
                  <h1 className="text-base font-semibold text-gray-900 leading-tight">Team</h1>
                  <p className="text-[11px] text-gray-500 leading-tight">Manage members & roles</p>
                </div>
              </div>

              {/* Inline Stats - Compact */}
              <div className="hidden lg:flex items-center space-x-3 pl-5 border-l border-gray-200">
                <div className="flex items-center space-x-1">
                  <span className="text-xs font-semibold text-gray-900">{stats.total}</span>
                  <span className="text-[10px] text-gray-400">Total</span>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-xs font-semibold text-green-600">{stats.active}</span>
                  <span className="text-[10px] text-gray-400">Active</span>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-xs font-semibold text-yellow-600">{stats.pending}</span>
                  <span className="text-[10px] text-gray-400">Pending</span>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-xs font-semibold text-gray-500">{stats.inactive}</span>
                  <span className="text-[10px] text-gray-400">Inactive</span>
                </div>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center space-x-2">
              <button
                onClick={loadTeamData}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>

              {currentUserCanManage && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center px-3 py-1.5 text-[11px] font-medium rounded-md bg-brand-600 text-white hover:bg-brand-700 transition-colors"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Member
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Compact Toolbar - Filters */}
      <div className="flex-none bg-white border-b border-gray-100 px-5 py-2">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Input
              placeholder="Search members..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              leftElement={<Search className="h-3.5 w-3.5 text-gray-400 ml-2" />}
              className="text-xs py-1.5"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as any)}
            className="px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-brand-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
          </select>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value as any)}
            className="px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-brand-500"
          >
            <option value="all">All Roles</option>
            <option value="project_manager">Project Manager</option>
            <option value="architect">Architect</option>
            <option value="engineer">Engineer</option>
            <option value="contractor">Contractor</option>
            <option value="site_supervisor">Site Supervisor</option>
            <option value="client">Client</option>
            <option value="viewer">Viewer</option>
          </select>

          {/* Member Count */}
          <span className="text-[10px] text-gray-400 ml-auto">
            {filteredMembers.length} members
          </span>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 p-4 bg-gray-50 overflow-auto">
        {/* Pending Invitations - Compact */}
        {invitations.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-4">
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900 flex items-center">
                <Mail className="h-3.5 w-3.5 mr-1.5 text-brand-500" />
                Pending Invitations
              </h3>
              <span className="text-[10px] text-gray-400">{invitations.length}</span>
            </div>

            <div className="divide-y divide-gray-100">
              {invitations.map(invitation => {
                const getStatusDisplay = (status: string) => {
                  switch (status) {
                    case 'pending':
                      return { bg: 'bg-yellow-50', text: 'text-yellow-600', label: 'Pending' };
                    case 'expired':
                      return { bg: 'bg-red-50', text: 'text-red-600', label: 'Expired' };
                    case 'accepted':
                      return { bg: 'bg-green-50', text: 'text-green-600', label: 'Accepted' };
                    case 'declined':
                      return { bg: 'bg-gray-50', text: 'text-gray-600', label: 'Declined' };
                    default:
                      return { bg: 'bg-yellow-50', text: 'text-yellow-600', label: 'Pending' };
                  }
                };

                const statusDisplay = getStatusDisplay(invitation.status);

                return (
                  <div
                    key={invitation.id}
                    className="px-4 py-2.5 flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      <div
                        className={`w-6 h-6 ${statusDisplay.bg} rounded-full flex items-center justify-center flex-none`}
                      >
                        <Mail className={`h-3 w-3 ${statusDisplay.text}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-xs font-medium text-gray-900 truncate">
                            {invitation.email}
                          </p>
                          <span
                            className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${statusDisplay.bg} ${statusDisplay.text}`}
                          >
                            {statusDisplay.label}
                          </span>
                        </div>
                        <p className="text-[10px] text-gray-500">
                          {invitation.role.replace(/_/g, ' ')} • {formatDate(invitation.invitedAt)}
                        </p>
                      </div>
                    </div>

                    {currentUserCanManage &&
                      (invitation.status === 'pending' || invitation.status === 'expired') && (
                        <div className="flex items-center space-x-1 flex-none">
                          <button
                            onClick={() => handleResendInvitation(invitation)}
                            className="px-2 py-1 text-[10px] text-brand-600 hover:bg-brand-50 rounded transition-colors"
                          >
                            Resend
                          </button>
                          <button
                            onClick={() => handleCancelInvitation(invitation)}
                            className="px-2 py-1 text-[10px] text-red-600 hover:bg-red-50 rounded transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Team Members */}
        {isLoading ? (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 flex items-center justify-center">
            <LoadingSpinner size="md" />
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center">
            <Users className="mx-auto h-8 w-8 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No team members found</h3>
            <p className="mt-1 text-xs text-gray-500">
              {searchQuery || statusFilter !== 'all' || roleFilter !== 'all'
                ? 'Try adjusting your filters.'
                : 'Add the first team member to get started.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-gray-100">
              {paginatedMembers.map(member => (
                <div key={member.id} className="bg-white">
                  <TeamMemberCard
                    member={member}
                    onEdit={member => {
                      setEditingMember(member);
                      setShowAddModal(true);
                    }}
                    onRemove={handleRemoveMember}
                    onStatusToggle={handleStatusToggle}
                    currentUserCanManage={currentUserCanManage}
                  />
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t border-gray-100 px-4 py-2 bg-gray-50">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Drawer */}
      <AddTeamMemberDrawer
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingMember(null);
        }}
        onSubmit={editingMember ? handleEditMember : handleAddMember}
        projectId={projectId}
        editingMember={editingMember}
        mode={editingMember ? 'edit' : 'add'}
      />
    </div>
  );
};

export default TeamManagement;
